/**
 * leaderboardView — shared leaderboard rendering (M3 extraction).
 *
 * The table/empty-state markup lived inside VehicleSelect.renderLeaderboard;
 * it MOVED here unchanged so the classic LEADERS tab and the new RANKINGS
 * screen render through one code path (no copy-pasted business logic).
 * The podium strip is RANKINGS-only chrome built from the same entries.
 */

import type { LeaderboardEntry } from "../auth/AuthClient.js";

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

/** K/D display: two decimals when deaths exist, whole kills otherwise. */
export function formatKd(kills: number, deaths: number): string {
  return deaths > 0 ? (kills / deaths).toFixed(2) : kills.toFixed(0);
}

/** Row accent class for podium ranks (gold/silver/bronze), "" otherwise. */
export function rankClass(rank: number): string {
  return rank === 1 ? "lb-rank-gold" : rank === 2 ? "lb-rank-silver" : rank === 3 ? "lb-rank-bronze" : "";
}

/**
 * Full leaderboard content: empty-state div or the classic table.
 * Output is byte-identical to the old VehicleSelect.renderLeaderboard.
 */
export function buildLeaderboardContentHtml(entries: LeaderboardEntry[]): string {
  if (entries.length === 0) {
    return `<div class="lb-empty">No ranked players yet — be the first!</div>`;
  }
  const rows = entries
    .map((e) => {
      const kd = formatKd(e.kills, e.deaths);
      return (
        `<tr class="${rankClass(e.rank)}">` +
        `<td class="lb-rank">${e.rank}</td>` +
        `<td class="lb-player">${escapeHtml(e.username)}</td>` +
        `<td class="lb-kills">${e.kills}</td>` +
        `<td class="lb-kd">${kd}</td>` +
        `<td class="lb-wins">${e.wins}</td>` +
        `</tr>`
      );
    })
    .join("");
  return (
    `<table class="lb-table">` +
    `<thead><tr><th>#</th><th>PLAYER</th><th>KILLS</th><th>K/D</th><th>WINS</th></tr></thead>` +
    `<tbody>${rows}</tbody>` +
    `</table>`
  );
}

/** Top-3 (by rank field) in display order 2-1-3 — champion on the center step. */
export function podiumOrder(entries: LeaderboardEntry[]): LeaderboardEntry[] {
  const byRank = new Map<number, LeaderboardEntry>();
  for (const e of entries) {
    if (e.rank >= 1 && e.rank <= 3 && !byRank.has(e.rank)) byRank.set(e.rank, e);
  }
  const order = [2, 1, 3];
  const out: LeaderboardEntry[] = [];
  for (const r of order) {
    const e = byRank.get(r);
    if (e) out.push(e);
  }
  return out;
}

/** RANKINGS podium strip (empty string when there is no rank 1–3 data). */
export function buildPodiumHtml(entries: LeaderboardEntry[]): string {
  const podium = podiumOrder(entries);
  if (podium.length === 0) return "";
  const slots = podium
    .map((e) => {
      const metal = e.rank === 1 ? "gold" : e.rank === 2 ? "silver" : "bronze";
      return (
        `<div class="rk-podium-slot rk-podium-${metal}">` +
        `<div class="rk-podium-name">${escapeHtml(e.username)}</div>` +
        `<div class="rk-podium-kills">${e.kills} KILLS · ${e.wins} WINS</div>` +
        `<div class="rk-podium-step"><span class="rk-podium-rank">${e.rank}</span></div>` +
        `</div>`
      );
    })
    .join("");
  return `<div class="rk-podium">${slots}</div>`;
}
